/**
 * 평가요소 예시 제안 API 클라이언트
 *
 * - POST /api/saenggibu/evaluation-elements/suggest
 * - 인증은 생기부 생성과 같은 auth-optional. 로그인 상태면 Bearer 토큰을 붙인다.
 * - 클라이언트 원문(text)은 보내지 않는다. 서버가 key/code로 데이터셋 원문을 다시 찾는다.
 * - 모든 함수는 throw하지 않고 `{ ok, data }` 또는 `{ ok: false, error: { code, message, status } }`를 돌려준다.
 * - 서버는 데이터셋에 없는 코드를 활동명의 `[코드] 문장`(교사 문장)으로 대신한다. 그런 참조는
 *   `data.teacher_grounded_refs`, 문장도 없어 빠진 참조는 `data.dropped_refs`로 온다(둘 다 없으면 400 invalid-refs).
 * - 재시도: 네트워크 오류·5xx는 1회만(서버가 Gemini 재시도·폴백을 이미 한다). 타임아웃은 재시도하지 않는다
 *   (같은 호출을 반복해도 비용만 두 배). 타임아웃은 `calcApiTimeout('evaluation-elements-suggest')`.
 */

import { normalizeCurriculumId } from '../config/curricula';
import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { calcApiTimeout, fetchWithRetry } from '../utils/fetchWithRetry';

export const SUGGEST_PATH = '/api/saenggibu/evaluation-elements/suggest';
const MAX_REFS = 10;
const MAX_ACTIVITY_TEXT_CHARS = 4000;

export const EVALUATION_ELEMENTS_ERROR_CODE = {
  CANCELLED: 'cancelled',
  INVALID_REFS: 'invalid-refs',
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  UNAVAILABLE: 'unavailable',
  UNKNOWN: 'unknown',
};

const MESSAGES = {
  [EVALUATION_ELEMENTS_ERROR_CODE.CANCELLED]: '요청을 취소했어요.',
  [EVALUATION_ELEMENTS_ERROR_CODE.INVALID_REFS]: "성취기준을 찾지 못했어요. 코드 뒤에 성취기준 문장을 함께 적거나 '성취기준 찾기'로 넣어주세요.",
  [EVALUATION_ELEMENTS_ERROR_CODE.NETWORK]: '연결을 확인한 뒤 다시 시도해주세요.',
  [EVALUATION_ELEMENTS_ERROR_CODE.TIMEOUT]: '지금 시간이 오래 걸려요. 잠시 후 다시 시도해주세요.',
  [EVALUATION_ELEMENTS_ERROR_CODE.UNAVAILABLE]: '지금은 예시를 만들 수 없어요. 잠시 후 다시 시도해주세요.',
  [EVALUATION_ELEMENTS_ERROR_CODE.UNKNOWN]: '예시를 만들지 못했어요. 다시 시도해주세요.',
};

const UNAVAILABLE_STATUSES = new Set([404, 429, 501, 502, 503]);
const AUTH_MESSAGE = '로그인 정보를 확인하지 못했어요. 다시 로그인한 뒤 시도해주세요.';

const normalizeCurriculum = normalizeCurriculumId;

const buildError = (code, { message, status = 0, invalid } = {}) => ({
  error: {
    code,
    invalid: Array.isArray(invalid) ? invalid : undefined,
    message: message || MESSAGES[code] || MESSAGES[EVALUATION_ELEMENTS_ERROR_CODE.UNKNOWN],
    status,
  },
  ok: false,
});

const getAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  const user = auth?.currentUser;
  if (!user || typeof user.getIdToken !== 'function') return headers;
  try {
    const token = await user.getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // 토큰을 못 받아도 auth-optional 엔드포인트라 익명으로 진행한다
  }
  return headers;
};

/** 서버 요청 본문. text는 버리고 key/code만 보낸다. */
export const buildSuggestRequestBody = ({
  activityText,
  category,
  refs,
  schoolLevel,
  subject,
} = {}) => {
  const seen = new Set();
  const cleanRefs = [];
  (Array.isArray(refs) ? refs : []).forEach((ref) => {
    const key = String(ref?.key || '').trim();
    const code = String(ref?.code || '').trim();
    if (!key && !code) return;
    const curriculum = normalizeCurriculum(ref?.curriculum);
    const identity = `${curriculum}:${key || `code:${code}`}`;
    if (seen.has(identity)) return;
    seen.add(identity);
    const entry = { curriculum };
    if (key) entry.key = key;
    if (code) entry.code = code;
    cleanRefs.push(entry);
  });
  const body = { refs: cleanRefs.slice(0, MAX_REFS) };
  if (schoolLevel) body.school_level = String(schoolLevel);
  if (subject) body.subject = String(subject).slice(0, 40);
  if (category) body.category = String(category).slice(0, 40);
  const activity = String(activityText || '').trim();
  if (activity) body.activity_text = activity.slice(0, MAX_ACTIVITY_TEXT_CHARS);
  return body;
};

const errorFromStatus = (status, payload) => {
  const detail = typeof payload?.detail === 'string' ? payload.detail : '';
  if (status === 400) {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.INVALID_REFS, { invalid: payload?.invalid, message: detail, status });
  }
  if (status === 504 || payload?.code === 'timeout') {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.TIMEOUT, { status });
  }
  if (status === 401) {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.UNAVAILABLE, { message: AUTH_MESSAGE, status });
  }
  if (UNAVAILABLE_STATUSES.has(status)) {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.UNAVAILABLE, { message: detail, status });
  }
  return buildError(EVALUATION_ELEMENTS_ERROR_CODE.UNKNOWN, { message: detail, status });
};

/**
 * @param {{ refs: Array<{curriculum?: string, key?: string, code?: string}>, schoolLevel?: string, subject?: string, category?: string, activityText?: string }} input
 * @param {{ signal?: AbortSignal }} options
 */
export const suggestEvaluationElements = async (input, { signal } = {}) => {
  const body = buildSuggestRequestBody(input);
  if (body.refs.length === 0) {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.INVALID_REFS);
  }

  const headers = await getAuthHeaders();
  let response;
  try {
    response = await fetchWithRetry(
      getApiUrl(SUGGEST_PATH),
      { body: JSON.stringify(body), headers, method: 'POST', signal },
      {
        maxRetries: 1,
        retryOnTimeout: false,
        timeoutMs: calcApiTimeout('evaluation-elements-suggest'),
      },
    );
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) {
      return buildError(EVALUATION_ELEMENTS_ERROR_CODE.CANCELLED);
    }
    if (String(error?.message || '').includes('시간이 초과')) {
      return buildError(EVALUATION_ELEMENTS_ERROR_CODE.TIMEOUT);
    }
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.NETWORK);
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    return errorFromStatus(response.status, payload);
  }
  const suggestions = Array.isArray(payload?.suggestions)
    ? payload.suggestions.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  if (suggestions.length === 0) {
    return buildError(EVALUATION_ELEMENTS_ERROR_CODE.UNKNOWN, { status: response.status });
  }
  return {
    data: {
      ...payload,
      dropped_refs: Array.isArray(payload?.dropped_refs) ? payload.dropped_refs : [],
      grounded_on: Array.isArray(payload?.grounded_on) ? payload.grounded_on : [],
      suggestions,
      teacher_grounded_refs: Array.isArray(payload?.teacher_grounded_refs) ? payload.teacher_grounded_refs : [],
    },
    ok: true,
  };
};
